/**
 * Contents of this file were produced with a help of a code generation tool and
 * subsequently reviewed and edited by a human.
 *
 * While some of the code was created by an AI assisted service, manual adjustments
 * have been made to ensure correctness, readability, functionality and compliance
 * to Avalara's coding standards and guidelines. Any future modifications should
 * be approached with caution to avoid overwriting the manual changes.
 *
 * This source code is owned by Avalara Inc. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Proprietary and confidential.
 *
 * For more information, refer to the documentation or contact the development team.
 */

import {
    AuiFooter,
    AuiHeader,
    AuiLanguageSupportDialog,
    AuiSessionTimeout,
    AuiSidenav,
    AuiSupernav,
    SLoader,
    SToasts,
} from "@avalara/skylab-sdk/react";
import debounce from "lodash/debounce";
import React, {
    MouseEvent,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { testIDs } from "../../../end-to-end-tests/constants/testIDs";
import { getCertificateStatusesAsync } from "../../app/certificateSlice";
import {
    fetchAllPreferences,
    saveLocalePreference,
} from "../../app/preferencesSlice/preferencesActions";
import { useLocale } from "../../i18n/useLocale";
import { fetchRetailSettings } from "../../app/retailSettingsSlice";
import {
    getSessionAsync,
    refreshSessionContextAsync,
    selectSessionUIFields,
    selectSkylabHeader,
} from "../../app/sessionSlice";
import type { SessionState } from "../../app/sessionSlice/types";
import { fetchSettingsAPI, setPreviousPath } from "../../app/settingsSlice";
import { AppDispatch } from "../../app/types";
import getConfig from "../../config";
import featureFlag from "../../featureToggler/featureFlag";
import "../../globalTypes";
import { useIsNoAccessUser } from "../../hooks/useAccessControl";
import { useIsAuthorized } from "../../hooks/useIsAuthorized";
import { useTokenProvider, type TokenProviderFunction } from "../../hooks/useTokenProvider";
import isOneWebDomainRuntime from "../../shared/avaAppRuntime";
import {
    AVA_APP_NAME,
    avataxAPUrls,
    PREVIOUS_ACCOUNT_ID_COOKIE_NAME,
    SessionRefreshState,
    SessionRefreshStateType,
} from "../../shared/constants";
import { useLocaleOptions } from "../../i18n/useLocaleOptions";
import { cleanupSessionRefreshTimeout } from "../../shared/sessionTimeoutCleanup";
import {
    clearAccountSwitchStorageKeys,
    clearLocalStorage,
    getCookie,
    retainCriticalData,
    setCookie,
    urlToPageMap,
} from "../../shared/Utils";
import zoominWidget from "../../shared/zoominWidget";
import ErrorBoundaryWrapper from "../errorHandling/errorBoundary";
import Navigation from "../navigation/Navigation";
import { NavigationVisibilityContext } from "../navigation/NavigationVisibilityProvider";
import { NavigationVisibilityContextType } from "../navigation/types";
import Provision from "../provision/Provision";
import { useSideNavFeatureFlag } from "../sharedComponents/useSideNavFeatureFlag";
import { useLocaleSwitcherFeatureFlag } from "../sharedComponents/useLocaleSwitcherFeatureFlag";
import { useWaffleMenuFeatureFlag } from "../sharedComponents/useWaffleMenuFeatureFlag";
import AppVersion from "./AppVersion";

enum navTabs {
    EXEMPTIONS = "aui-supernav.exemptions",
    SETTINGS = "aui-supernav.settings",
}

// Config is retrieved lazily inside functions to support runtime configuration
const getLazyConfig = () => {
    const config = getConfig();
    return {
        appEnv: config.appEnv,
        ecmApiHost: config.hostnames.ecmApiHost,
        identity: config.hostnames.identity,
    };
};

const getMainContentClassName = (isRetailView: boolean, hideNav: boolean): string => {
    if (isRetailView || !hideNav) {
        return "dl-content flex dl-flex-fill-height";
    }
    return "flex dl-flex-fill-height";
};

// Helper function to determine app DOM content
const getAppDOMContent = (
    session: Pick<SessionState, "accountId" | "provisionStatus">,
    isNoAccessUserFlag: boolean,
    currentLocation: string,
    isSideNavEnabled: boolean,
    isLoading: boolean,
    hasInitialSessionLoaded: boolean
): React.JSX.Element | null => {
    if (isLoading || !hasInitialSessionLoaded) {
        return (
            <div className="loader-container">
                <h1>Loading ...</h1>
                <SLoader id="page-loader" className="large" aria-live="polite" loading />
            </div>
        );
    }

    if (
        !isLoading &&
        ((session.accountId && (session.provisionStatus === "Provisioned" || isNoAccessUserFlag)) ||
            currentLocation.includes("health-check"))
    ) {
        return <Navigation isSideNavEnabled={isSideNavEnabled} />;
    }

    if (session.accountId) {
        return (
            <div className={`margin-top-lg provision-container`}>
                <Provision />
            </div>
        );
    }

    return null;
};

function Application(): React.JSX.Element {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const session = useSelector(selectSessionUIFields, shallowEqual) as Partial<SessionState>;

    const header = useSelector(selectSkylabHeader, shallowEqual);

    const { hideNav }: NavigationVisibilityContextType = useContext(NavigationVisibilityContext);
    const isNoAccessUserFlag = useIsNoAccessUser();
    const currentLocation: string = window.location.href;
    const isRetailView =
        !currentLocation.includes(urlToPageMap.settings) &&
        currentLocation.includes(urlToPageMap.retail);
    const sideNavRef = useRef<React.ElementRef<typeof AuiSidenav>>(null);
    const superNavRef = useRef<React.ElementRef<typeof AuiSupernav>>(null);
    const [navActiveTab, setNavActiveTab] = useState<navTabs>(navTabs.EXEMPTIONS);

    const shouldSelectSettingsInSupernav = useIsAuthorized(
        featureFlag.topNav.activateSettingsTab,
        true
    );
    const errorBoundaryRef = useRef<{ removeError?: () => void }>();
    const zoomInWidgetId17 = useIsAuthorized(featureFlag.zoomInWidget.id17, true);
    const zoomInWidgetId18 = useIsAuthorized(featureFlag.zoomInWidget.id18, true);
    const zoomInWidgetId23 = useIsAuthorized(featureFlag.zoomInWidget.id23, true);
    const canAccessRetail = useIsAuthorized(featureFlag.retail.index, true);
    const isSideNavEnabled = useSideNavFeatureFlag();
    const isWaffleMenuEnabled = useWaffleMenuFeatureFlag();
    const isLocaleSwitcherEnabled = useLocaleSwitcherFeatureFlag();
    const localeOptions = useLocaleOptions();
    const activeLocale = useLocale();
    // Skip the first s-localeselect event which is always an init broadcast from
    // Skylab (watchUser fires it on mount). Only subsequent events are genuine
    // user selections.
    const localeSelectInitFiredRef = useRef<boolean>(false);
    const [refreshState, setRefreshState] = useState<SessionRefreshStateType>(
        SessionRefreshState.Idle
    );
    const [hasInitialSessionLoaded, setHasInitialSessionLoaded] = useState(false);
    const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isLoading: boolean =
        (session.isLoading ?? false) || refreshState === SessionRefreshState.Refreshing;

    const logout = (): void => {
        // Clean up session refresh timeout to prevent memory leaks
        cleanupSessionRefreshTimeout();

        retainCriticalData();
        clearLocalStorage();
        const { appEnv, ecmApiHost } = getLazyConfig();
        const baseURL: string =
            appEnv === "local" ? `http://${ecmApiHost}/api` : `https://${ecmApiHost}/api`;
        window.location.href = `${baseURL}/v3/auth/logout`;
    };

    useEffect(() => {
        if (!currentLocation.includes("/health-check")) dispatch(getSessionAsync());
        const documentHeader = document.querySelector("aui-header");
        if (documentHeader) {
            documentHeader.addEventListener("click", (event: Event) => {
                const target = event.target as HTMLElement;
                if (target && target.textContent === "Sign out") {
                    logout();
                }
            });
        }
    }, [dispatch, currentLocation]);

    useEffect(() => {
        if (!hasInitialSessionLoaded && session.isAuthenticated) {
            setHasInitialSessionLoaded(true);
        }
    }, [session.isAuthenticated, hasInitialSessionLoaded]);

    // Track accountId changes using cookies to detect account switch (not company switch)
    // Cookies persist across page reloads and redirects within the same domain
    useEffect(() => {
        const currentAccountId = session.accountId;

        // Skip if accountId is not yet loaded
        if (!currentAccountId) {
            return;
        }

        // Get the previously stored accountId from cookie
        const previousAccountId = getCookie(PREVIOUS_ACCOUNT_ID_COOKIE_NAME);

        // If we have a previous accountId and it's different from current, account has changed
        if (previousAccountId && previousAccountId !== currentAccountId) {
            // Account has changed - clear account-specific localStorage keys
            clearAccountSwitchStorageKeys();
        }

        // Store current accountId in cookie for next comparison (persists across reloads/redirects)
        // Set cookie to expire in 30 days (long enough to survive redirects but not permanent)
        setCookie(PREVIOUS_ACCOUNT_ID_COOKIE_NAME, currentAccountId, 30);
    }, [session.accountId]);

    useEffect(() => {
        if (session.activeCompany?.id && session.provisionStatus === "Provisioned") {
            dispatch(fetchSettingsAPI(true));
            dispatch(getCertificateStatusesAsync());
            dispatch(fetchAllPreferences());
            if (canAccessRetail) {
                dispatch(fetchRetailSettings());
            }
        }
        if (session.activeCompany?.name) {
            document.title = `Avatax | ${session.activeCompany.name}`;
        }
    }, [
        dispatch,
        session.activeCompany?.id,
        session.activeCompany?.name,
        session.provisionStatus,
        session.subscriptions,
        canAccessRetail,
    ]);

    /**
     * Handle company change events from Skylab SDK's AuiSidenav/AuiHeader components.
     *
     * Per Skylab SDK docs (https://skylabsdk.avalara.io/views/layout/sidenav/):
     * - The s-change-company event fires with { detail: { company, initialLoad } }
     * - initialLoad: true = sidenav initially identifying active company on page load
     * - initialLoad: false = user explicitly changed company via dropdown
     *
     * We only trigger refresh when initialLoad is false (actual user action).
     * Debounced to prevent rapid successive events from triggering multiple refreshes.
     */
    const debouncedRefresh = useMemo(
        () =>
            debounce((event: Event) => {
                const customEvent = event as CustomEvent<{
                    company: unknown;
                    initialLoad: boolean;
                }>;

                // Per Skylab SDK docs: only refresh when user explicitly changes company
                // Skip if initialLoad is true (sidenav initially identifying active company)
                // Also skip if event detail is missing (malformed event)
                if (!customEvent.detail || customEvent.detail.initialLoad) {
                    return;
                }

                if (refreshState === SessionRefreshState.Refreshing) {
                    return;
                }

                setRefreshState(SessionRefreshState.Refreshing);

                refreshTimeoutRef.current = setTimeout(() => {
                    setRefreshState(SessionRefreshState.Idle);
                }, 15000);

                (async () => {
                    try {
                        const result = (await dispatch(refreshSessionContextAsync())) as boolean;
                        if (result) {
                            // Use SPA navigation instead of hard reload to preserve state
                            navigate("/", { replace: true });
                            // Refresh session state after navigation
                            await dispatch(getSessionAsync(true));
                        }
                    } finally {
                        if (refreshTimeoutRef.current) {
                            clearTimeout(refreshTimeoutRef.current);
                            refreshTimeoutRef.current = null;
                        }
                        setRefreshState(SessionRefreshState.Idle);
                    }
                })();
            }, 300),
        [dispatch, navigate, refreshState]
    );

    useEffect(() => {
        document.addEventListener("s-change-company", debouncedRefresh);

        return () => {
            document.removeEventListener("s-change-company", debouncedRefresh);
            debouncedRefresh.cancel();
            if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current);
                refreshTimeoutRef.current = null;
            }
        };
    }, [debouncedRefresh]);

    useEffect(() => {
        const handleLocaleSelect = (event: Event): void => {
            const customEvent = event as CustomEvent<{ locale: string }>;
            const locale: string = customEvent.detail?.locale;
            if (!locale) return;

            // Skylab's aui-locale-switcher fires s-localeselect once on mount via
            // watchUser() to broadcast its initially resolved locale. Skip that
            // first event — it reflects the identity-token locale, not the ECM
            // preference. Only subsequent events are genuine user selections.
            if (!localeSelectInitFiredRef.current) {
                localeSelectInitFiredRef.current = true;
                return;
            }

            dispatch(saveLocalePreference(locale));
        };

        document.addEventListener("s-localeselect", handleLocaleSelect);

        return () => {
            document.removeEventListener("s-localeselect", handleLocaleSelect);
        };
    }, [dispatch]);

    const getCurrentNavRef = useCallback((): HTMLElement | null => {
        return isSideNavEnabled ? sideNavRef.current : superNavRef.current;
    }, [isSideNavEnabled]);

    const setActiveMenuTab = useCallback(
        (currentURL: string): void => {
            const currentNav = getCurrentNavRef();
            if (currentNav) {
                const isMatch: boolean = avataxAPUrls.some((url: string) =>
                    currentURL.includes(url)
                );
                if (shouldSelectSettingsInSupernav && isMatch) setNavActiveTab(navTabs.SETTINGS);
                else setNavActiveTab(navTabs.EXEMPTIONS);
            }
        },
        [shouldSelectSettingsInSupernav, getCurrentNavRef]
    );

    useEffect(() => {
        setActiveMenuTab(window.location.pathname);
        if (getLazyConfig().appEnv !== "local" && session.isAuthenticated) {
            zoominWidget({ zoomInWidgetId17, zoomInWidgetId18, zoomInWidgetId23 });
        }
    }, [
        session.isAuthenticated,
        setActiveMenuTab,
        zoomInWidgetId17,
        zoomInWidgetId18,
        zoomInWidgetId23,
    ]);

    const tokenProvider = useTokenProvider();

    useEffect(() => {
        if (!isOneWebDomainRuntime()) {
            const ipuaPanel = document.querySelector("ipua-panel") as HTMLElement & {
                getaccesstoken?: TokenProviderFunction;
            };
            if (ipuaPanel && session.hasToken) {
                ipuaPanel.getaccesstoken = tokenProvider;
            }
        }
        dispatch(getSessionAsync());
        // tokenProvider is intentionally excluded from deps - it's a stable reference that only
        // changes when activeCompany.id changes, and including it would cause unnecessary re-runs
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, session.hasToken]);

    const handleNavigation = useCallback(
        (nativeMouseEvent: MouseEvent, url: string): void => {
            const uri = new URL(url);
            // Legacy standalone: hostname contains "exemptions" (e.g. qa.exemptions.avalara.io)
            // AvaApp: same origin with path starting under the app basename (/exemptions/...)
            const avaAppBasename = `/${AVA_APP_NAME}`;
            const isLegacyInternalNav = uri.hostname.includes("exemptions");
            const isAvaAppInternalNav =
                uri.origin === window.location.origin && uri.pathname.startsWith(avaAppBasename);

            if (isLegacyInternalNav || isAvaAppInternalNav) {
                nativeMouseEvent.preventDefault();
                errorBoundaryRef.current?.removeError?.();
                if (
                    uri.pathname.includes("settings") &&
                    !window.location.pathname.includes("settings")
                ) {
                    dispatch(setPreviousPath(window.location.pathname));
                }
                // In AvaApp the router basename is "/exemptions", so strip it before
                // calling navigate() — otherwise the router can't match the route.
                const pathname =
                    isAvaAppInternalNav && uri.pathname.startsWith(avaAppBasename)
                        ? uri.pathname.slice(avaAppBasename.length) || "/"
                        : uri.pathname;
                navigate(`${pathname}${uri.search}`);
                // (Only for top nav) The menu dropdown doesn't close automatically on selecting an item. So, have to programmatically click the expanded menu button to close it.
                if (!isSideNavEnabled) {
                    const currentNav = getCurrentNavRef();
                    if (currentNav) {
                        Array.from(currentNav.getElementsByTagName("button"))
                            ?.find(
                                (button: HTMLButtonElement) =>
                                    button.getAttribute("aria-expanded") === "true"
                            )
                            ?.click();
                    }
                }
            }
        },
        [dispatch, navigate, isSideNavEnabled, getCurrentNavRef]
    );

    const navMemo = useMemo(
        () =>
            isSideNavEnabled ? (
                <AuiSidenav
                    active={navActiveTab}
                    requestCurrentAccessToken={tokenProvider}
                    ref={sideNavRef}
                    navHandler={handleNavigation}
                    data-testid={testIDs.shared.navigation}
                />
            ) : (
                <AuiSupernav
                    active={navActiveTab}
                    requestCurrentAccessToken={tokenProvider}
                    ref={superNavRef}
                    navHandler={handleNavigation}
                    data-testid={testIDs.shared.navigation}
                />
            ),
        [isSideNavEnabled, tokenProvider, navActiveTab, handleNavigation]
    );

    const auiSessionTimeoutMemo = useMemo(
        () => <AuiSessionTimeout onS-timeout={() => logout()} />,
        []
    );

    const appDOM: React.JSX.Element | null = getAppDOMContent(
        session,
        isNoAccessUserFlag,
        currentLocation,
        isSideNavEnabled,
        isLoading,
        hasInitialSessionLoaded
    );

    if (isOneWebDomainRuntime()) {
        return <ErrorBoundaryWrapper ref={errorBoundaryRef}>{appDOM}</ErrorBoundaryWrapper>;
    }

    return (
        <div
            className="app-container flex dl-flex-fill-height"
            data-testid={testIDs.common.container}>
            {session.isAuthenticated && session.hasToken ? (
                <AuiHeader
                    sideNavHeader={isSideNavEnabled}
                    appKey={header.appKey}
                    user={header.user}
                    logoutUrl={header.logoutUrl}
                    onS-navigate={e => retainCriticalData(e)}
                    integrateHelp={!isRetailView}
                    integrateWaffleMenu={isWaffleMenuEnabled}
                    waffleMenuActiveItem="avalara-portal"
                    integrateLocaleSwitcher={isLocaleSwitcherEnabled}
                    localeSwitcherOptionsList={
                        isLocaleSwitcherEnabled && localeOptions.length > 0
                            ? localeOptions
                            : undefined
                    }
                    requestCurrentAccessToken={tokenProvider}
                    className={
                        isRetailView
                            ? "hide-account-products-usage hide-account-support-cases hide-account-billing hide-account-change-password hide-account-id hide-account-my-downloads"
                            : ""
                    }
                    showChatButton={!isRetailView}
                    data-testid={testIDs.shared.header}
                />
            ) : null}
            {session.isAuthenticated && session.hasToken && isLocaleSwitcherEnabled ? (
                <AuiLanguageSupportDialog
                    currentLocale={activeLocale}
                    localeSwitcherOptionsList={localeOptions.length > 0 ? localeOptions : undefined}
                    data-testid={testIDs.languageSupportDialog.container}
                />
            ) : null}
            <div className={`app-content ${isSideNavEnabled ? "" : "flex dl-flex-fill-height"}`}>
                {session.hasToken && !hideNav && !isRetailView ? navMemo : null}
                <div
                    className={`main-content margin-top-lg ${
                        isSideNavEnabled
                            ? "dl-content-sidenav"
                            : getMainContentClassName(isRetailView, hideNav)
                    }`}>
                    <ErrorBoundaryWrapper ref={errorBoundaryRef}>{appDOM}</ErrorBoundaryWrapper>
                </div>
            </div>
            <SToasts role="status" />
            {!isSideNavEnabled ? <AuiFooter user={header.user} /> : null}
            {session.hasToken ? <ipua-panel product="ECM" url={getLazyConfig().identity} /> : null}
            {auiSessionTimeoutMemo}
            <AppVersion />
        </div>
    );
}

export default React.memo(Application);
